import { NgClass } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  HostBinding,
  Inject,
  input,
  Input,
  signal,
} from '@angular/core';
import {
  AiFeaturesService,
  IqBoostPromptLookupResult,
  IQBoostViewStore,
  LanguageHelper,
  MaximizerAiService,
} from '@maximizer/ai/data-access';
import { SessionService } from '@maximizer/core/shared/data-access';
import { CDN_URL, EntryType } from '@maximizer/core/shared/domain';
import { EmbeddedComponent } from '@maximizer/core/shared/ui/components/base';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import { ActionArgs, ActionEventType } from '@maximizer/core/shared/ui/config';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import { UI_PIPES } from '@maximizer/core/shared/ui/pipes';
import { isNullOrEmpty, sanitizeMarkdownToHtml } from '@maximizer/shared/util';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import {
  animationFrameScheduler,
  auditTime,
  catchError,
  filter,
  finalize,
  forkJoin,
  Observable,
  of,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { Helper, IQBoostInternalCommand } from '../classes/iqboost';
import { ClickableRefPipe } from '../pipes/clickable-ref.pipe';
import { DeAnonymizerPipe } from '../pipes/de-anonymizer.pipe';
import { EntityKeyRefPipe } from '../pipes/entity-key-ref.pipe';
import { SafeHtmlPipe } from '../pipes/safe-html.pipe';

class IQBoostArgs extends ActionArgs {
  constructor(
    public tag: string,
    public key: string,
    public type: string,
    public autoOpen: boolean = false,
  ) {
    super(ActionEventType.ShowDialog);
  }
}
@Component({
  selector: 'ai-widget-iqboost-summary',
  templateUrl: './widget-iqboost-summary.component.html',
  styleUrls: ['./widget-iqboost-summary.component.scss'],
  imports: [
    ClickableRefPipe,
    DeAnonymizerPipe,
    EntityKeyRefPipe,
    KENDO_BUTTON,
    KENDO_DIALOG,
    NgClass,
    SafeHtmlPipe,
    TranslatePipe,
    UI_LOADER,
    UI_KENDO,
    UI_PIPES,
  ],
})
export class WidgetIqboostSummaryComponent extends EmbeddedComponent<IQBoostArgs> {

  private readonly SHORT_SUMMARY_PROMPT_NAME = 'Short Summary';
  private readonly WAIT_TIME_BEFORE_LOADING = 1500;

  @HostBinding('class') hostClasses = 'ai-ng';
  aiClass = true;

  @Input() externalParent?: { actionCompletedEvent(args: IQBoostArgs): void };

  loading = signal(true);
  summary = signal('');
  key = input<string | null>();
  type = input<EntryType | null>();
  prompt = signal<IqBoostPromptLookupResult | undefined>(undefined);

  hasSummary = computed(() => !isNullOrEmpty(this.summary()));
  title = computed(() => Helper.getPromptNameFromTextPromptClient(this.prompt()));

  constructor(
    element: ElementRef,
    public readonly store: IQBoostViewStore,
    private readonly session: SessionService,
    private readonly aiFeaturesService: AiFeaturesService,
    private readonly maximizerAi: MaximizerAiService,
    @Inject(CDN_URL) readonly cdn: string,
  ) {
    super(element);

    effect(() => {
      const key = this.key();
      const type = this.type();

      if (key && type) {
        this.load(key, type);
      }
    });
  }

  override doAction(
    name: string,
    args: IQBoostArgs | boolean,
  ): void {
    if (name === ActionEventType.Show || name === ActionEventType.Initialize) {
      const iqBoostArgs = (args as IQBoostArgs);

      if (iqBoostArgs.key && iqBoostArgs.type) {
        this.load(iqBoostArgs.key, iqBoostArgs.type as EntryType);
      }
    }
    else if (name === IQBoostInternalCommand.SetAnonymized) {
      this.store.dataProcessingFeatureGroup().user.dataProcessingFeatures.anonymized = args as boolean;
      this.forcePipeReEvaluation();
    }
    else {
      super.doAction(name, args);
    }
  }

  toggleExpandedView(): void {
    this.store.setWidgetIsExpanded(!this.store.widgetIsExpanded());
  }

  openIqBoost(): void {
    const args = new IQBoostArgs(
      'maximizer-ai-iqboost',
      this.store.key() ?? '',
      'addressBook',
      true
    );

    if (this.externalParent) {
      this.externalParent.actionCompletedEvent(args);
    } else {
      this.actionCompleted.emit(args);
    }
  }

  private forcePipeReEvaluation(): void {
    const currentSummary = this.summary();
    if (currentSummary) {
      this.summary.set('');
      setTimeout(() => this.summary.set(currentSummary), 0);
    }
  }

  /** Loads Short Summary via generate-response. */
  load(key: string, type: EntryType): void {
    this.store.setMaximizerEntry(key, type);
    this.loading.set(true);

    this.addSubscription(
      timer(this.WAIT_TIME_BEFORE_LOADING).pipe(
        // 1.5 second delay to avoid loading data when the user is looking
        // for an entry using the Up and Down arrows in the Details tab
        switchMap(() =>
          forkJoin({
            shortSummaryPrompts: this.maximizerAi.lookupPrompts({
              project: this.aiFeaturesService.isSLE() ? 'IqBoostSLE' : 'IqBoost',
              filter: { name: [this.SHORT_SUMMARY_PROMPT_NAME] },
            }),
            session: this.session.getInfo(),
          }),
        ),
        switchMap(({ shortSummaryPrompts, session }) => {
          this.store.setSessionInfo(session);
          this.prompt.set(
            shortSummaryPrompts.find(
              (prompt) => prompt.name === this.SHORT_SUMMARY_PROMPT_NAME,
            ),
          );
          // Loads anonymizer state for the de-anonymizer pipe.
          return this.aiFeaturesService.processFiltersConfiguration(session, true);
        }),
        switchMap(() => this.requestSummary(key, type)),
        catchError(() => {
          this.summary.set('');
          return of(undefined);
        }),
        finalize(() => this.loading.set(false)),
      )
        .subscribe(),
    );
  }

  private requestSummary(key: string, type: EntryType): Observable<unknown> {
    const request = {
      entityKey: key,
      promptName: this.SHORT_SUMMARY_PROMPT_NAME,
      entityType: type,
      language: LanguageHelper.language(),
    };

    // Paint each coalesced chunk; drop the loader on the first token.
    let content = '';
    return this.maximizerAi.generateResponse(request).pipe(
      filter((chunk) => !!chunk.content),
      tap((chunk) => (content += chunk.content ?? '')),
      auditTime(0, animationFrameScheduler),
      tap(() => {
        this.loading.set(false);
        this.summary.set(sanitizeMarkdownToHtml(content));
      }),
    );
  }
}
